import { useDeferredValue, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiFetch } from '../../services/apiUtils';
import { DetailGrid, DrawerSection, EmptyState, StatusBadge } from './ui';
import { leadTypeLabel } from './leadAdapters';

export type ContactMarketingStatus = 'CLEAR' | 'DO_NOT_CONTACT' | 'REVIEW_REQUIRED';

export type ContactIdentifier = {
  id: string;
  type: string;
  provider: string | null;
  rawValue: string;
  normalizedValue: string;
  verified: boolean;
  primary: boolean;
};

export type CanonicalContact = {
  id: string;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  preferredName: string | null;
  fullName: string;
  preferredContactMethod: string | null;
  primaryEmail: string | null;
  primaryMobile: string | null;
  identifiers: ContactIdentifier[];
  marketing: {
    overall: ContactMarketingStatus;
    source: string;
    scope: string | null;
    channels: Record<string, { status: ContactMarketingStatus; source: string; scope: string | null; recordedAt: string | null }>;
  };
  assignment: {
    associateId: string | null;
    associateName: string | null;
    marketCentreId: string | null;
    marketCentreName: string | null;
    teamId: string | null;
    teamName: string | null;
  };
  sources: string[];
  enquiryCount: number;
  lastEnquiryAt: string | null;
  createdAt: string;
  updatedAt: string;
  associatedAgentCount?: number;
  primaryAgent?: { associateId: string; name: string | null; kwuid: string | null } | null;
  marketCentres?: string[];
};

export type AssociatedAgent = { associateId: string | null; name: string | null; kwuid: string | null; marketCentreName: string | null; enquiryCount: number };

export type CanonicalContactDetail = CanonicalContact & {
  assignments: Array<Record<string, unknown>>;
  enquiries: Array<Record<string, unknown>>;
  associatedAgents?: AssociatedAgent[];
  consent: Array<Record<string, unknown>>;
  preferenceDetails: Array<Record<string, unknown>>;
  integrations: { command: string; google: string };
};

type ContactsResponse = {
  rows: CanonicalContact[];
  total: number;
  limit: number;
  offset: number;
};

const inputClass = 'mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-800';
const labelClass = 'text-xs font-semibold uppercase tracking-wide text-slate-500';

function formatDate(value: unknown): string {
  if (!value) return 'Not recorded';
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function sourceLabel(source: string): string {
  return ({ PROPERTY24: 'Property24', PRIVATE_PROPERTY: 'Private Property', KWHOMES: 'KWHomes', COMMAND: 'Command', INTERNAL: 'Internal' } as Record<string, string>)[source] ?? source;
}

function enquiryListingContext(enquiry: Record<string, unknown>): string {
  const reference = String(enquiry.listing_number ?? enquiry.source_listing_reference ?? '').trim();
  const title = String(enquiry.property_title ?? '').trim();
  return [reference, ...(title && title !== reference ? [title] : [])].filter(Boolean).join(' · ') || 'No listing recorded';
}

function marketingTone(status: ContactMarketingStatus): 'good' | 'danger' | 'warning' {
  if (status === 'CLEAR') return 'good';
  if (status === 'DO_NOT_CONTACT') return 'danger';
  return 'warning';
}

function MarketingStatus({ status }: { status: ContactMarketingStatus }): JSX.Element {
  return <StatusBadge label={status.replace(/_/g, ' ')} tone={marketingTone(status)} />;
}

export function contactDisplayName(contact: Pick<CanonicalContact, 'fullName' | 'firstName' | 'lastName' | 'preferredName'>): string {
  return contact.fullName?.trim() || [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim() || contact.preferredName?.trim() || 'Name not provided';
}

function agentLabel(name: string | null | undefined, kwuid: string | null | undefined): string {
  return [name?.trim() || 'Associate not recorded', kwuid ? `KWUID ${kwuid}` : null].filter(Boolean).join(' · ');
}

export function CanonicalContactsEmptyState(): JSX.Element {
  return (
    <EmptyState
      title="No canonical contacts yet"
      detail="Contacts will appear here as leads are ingested and identities are matched. Existing legacy portal leads remain available under Leads while canonical ingestion is disabled."
    />
  );
}

export function CanonicalContactsTable({ contacts, onOpen }: { contacts: CanonicalContact[]; onOpen: (contact: CanonicalContact) => void }): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-[1180px] w-full text-left text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-3">Contact</th>
            <th className="px-3 py-3">Primary email</th>
            <th className="px-3 py-3">Primary mobile</th>
            <th className="px-3 py-3">Marketing status</th>
            <th className="px-3 py-3">Associated agents</th>
            <th className="px-3 py-3">Market Centre</th>
            <th className="px-3 py-3">Lead sources</th>
            <th className="px-3 py-3 text-right">Enquiries</th>
            <th className="px-3 py-3">Last enquiry</th>
            <th className="px-3 py-3">Created</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {contacts.map((contact) => (
            <tr key={contact.id} className="hover:bg-slate-50">
              <td className="px-3 py-3">
                <button type="button" onClick={() => onOpen(contact)} className="text-left font-semibold text-sky-700 hover:underline">
                  {contactDisplayName(contact)}
                </button>
              </td>
              <td className="px-3 py-3 text-slate-700">{contact.primaryEmail ?? 'Not recorded'}</td>
              <td className="px-3 py-3 text-slate-700">{contact.primaryMobile ?? 'Not recorded'}</td>
              <td className="px-3 py-3"><MarketingStatus status={contact.marketing.overall} /></td>
              <td className="px-3 py-3 text-slate-700">
                {contact.primaryAgent ? agentLabel(contact.primaryAgent.name, contact.primaryAgent.kwuid) : contact.assignment.associateName ?? 'None recorded'}
                {(contact.associatedAgentCount ?? 0) > 1 ? <span className="block text-xs text-slate-500">+{(contact.associatedAgentCount ?? 0) - 1} more</span> : null}
              </td>
              <td className="px-3 py-3 text-slate-700">{contact.marketCentres?.length ? contact.marketCentres.join(', ') : contact.assignment.marketCentreName ?? 'None recorded'}</td>
              <td className="px-3 py-3">
                {contact.sources.length ? <div className="flex flex-wrap gap-1">{contact.sources.map((source) => <StatusBadge key={source} label={sourceLabel(source)} tone="info" />)}</div> : <span className="text-slate-400">None recorded</span>}
              </td>
              <td className="px-3 py-3 text-right tabular-nums">{contact.enquiryCount}</td>
              <td className="px-3 py-3 text-slate-600">{formatDate(contact.lastEnquiryAt)}</td>
              <td className="px-3 py-3 text-slate-600">{formatDate(contact.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CanonicalContactDrawer({ contact, loading, onClose }: { contact: CanonicalContactDetail | null; loading: boolean; onClose: () => void }): JSX.Element {
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/30" role="presentation" onMouseDown={onClose}>
      <aside className="h-full w-full max-w-3xl overflow-y-auto bg-white p-5 shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="canonical-contact-title" onMouseDown={(event) => event.stopPropagation()}>
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 pb-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Canonical contact</p>
            <h2 id="canonical-contact-title" className="mt-2 break-words text-xl font-semibold text-slate-900">{contact ? contactDisplayName(contact) : 'Loading contact...'}</h2>
            {contact ? <p className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500"><span className="min-w-0 break-all">{contact.primaryEmail ?? 'Email not recorded'}</span><span>{contact.primaryMobile ?? 'Mobile not recorded'}</span></p> : null}
          </div>
          <button type="button" onClick={onClose} className="shrink-0 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Close</button>
        </header>
        {loading || !contact ? <p className="py-8 text-sm text-slate-500" role="status">Loading contact details...</p> : (
          <div className="mt-5 space-y-5">
            <DrawerSection title="Overview">
              <DetailGrid items={[
                ['Full name', contactDisplayName(contact)],
                ['Created', formatDate(contact.createdAt)],
                ['Enquiries', String(contact.enquiryCount)],
                ['Last enquiry', formatDate(contact.lastEnquiryAt)],
              ]} />
              <div className="mt-4 border-t border-slate-200 pt-4">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Identifiers</h4>
                {contact.identifiers.length ? <div className="mt-2 divide-y divide-slate-100">{contact.identifiers.map((identifier) => (
                  <div key={identifier.id} className="flex flex-col gap-1 py-2.5 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                    <span className="min-w-0 break-all text-sm text-slate-800"><strong className="font-semibold text-slate-900">{identifier.type}</strong> · {identifier.rawValue}</span>
                    <span className="shrink-0 text-xs text-slate-500">{identifier.primary ? 'Primary' : 'Additional'} · {identifier.verified ? 'Verified' : 'Unverified'}</span>
                  </div>
                ))}</div> : <p className="mt-2 text-sm text-slate-500">No identifiers recorded.</p>}
              </div>
            </DrawerSection>
            <DrawerSection title="Marketing & Compliance">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 pb-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Overall eligibility</span>
                <MarketingStatus status={contact.marketing.overall} />
              </div>
              <div className="grid gap-x-6 sm:grid-cols-2">
                {['EMAIL', 'SMS', 'TELEPHONE', 'WHATSAPP'].map((channel) => (
                  <div key={channel} className="flex min-w-0 items-center justify-between gap-3 border-b border-slate-100 py-2.5 text-sm">
                    <span className="text-slate-700">{channel === 'TELEPHONE' ? 'Telephone' : channel.charAt(0) + channel.slice(1).toLowerCase()}</span>
                    <span className="shrink-0"><MarketingStatus status={contact.marketing.channels[channel]?.status ?? 'REVIEW_REQUIRED'} /></span>
                  </div>
                ))}
              </div>
              {contact.preferenceDetails.length ? <p className="mt-3 text-xs text-slate-500">{contact.preferenceDetails.length} authorized preference event{contact.preferenceDetails.length === 1 ? '' : 's'} available.</p> : null}
            </DrawerSection>
            <DrawerSection title="Associated Agents">
              {contact.associatedAgents?.length ? <div className="divide-y divide-slate-100">{contact.associatedAgents.map((agent) => (
                <div key={`${agent.associateId ?? 'none'}-${agent.marketCentreName ?? 'none'}`} className="flex flex-col gap-1 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                  <span className="min-w-0 text-sm"><strong className="break-words font-semibold text-slate-900">{agentLabel(agent.name, agent.kwuid)}</strong><span className="mt-0.5 block text-xs text-slate-500">{agent.marketCentreName ?? 'Market Centre not recorded'}</span></span>
                  <span className="shrink-0 text-xs text-slate-500">{agent.enquiryCount} enquir{agent.enquiryCount === 1 ? 'y' : 'ies'}</span>
                </div>
              ))}</div> : <p className="text-sm text-slate-500">No associate is linked to the enquiries you can view.</p>}
            </DrawerSection>
            <DrawerSection title="Enquiry History">
              {contact.enquiries.length ? <div className="divide-y divide-slate-100">{contact.enquiries.map((enquiry) => (
                <article key={String(enquiry.id)} className="py-3 text-sm first:pt-0 last:pb-0">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                    <strong className="text-slate-900">{sourceLabel(String(enquiry.provider ?? 'INTERNAL'))} · {leadTypeLabel(enquiry.source_lead_type == null ? null : String(enquiry.source_lead_type))}</strong>
                    <span className="text-xs text-slate-500">{formatDate(enquiry.occurred_at ?? enquiry.received_at)}</span>
                  </div>
                  <p className="mt-1 break-words font-medium leading-5 text-slate-700">{enquiryListingContext(enquiry)}</p>
                  <p className="mt-1 text-xs text-slate-500">Assigned: {String(enquiry.associate_name ?? 'Unassigned')}</p>
                  {enquiry.message ? <p className="mt-3 whitespace-pre-wrap break-words border-l-2 border-slate-200 pl-3 leading-6 text-slate-600">{String(enquiry.message)}</p> : null}
                </article>
              ))}</div> : <p className="text-sm text-slate-500">No enquiries recorded for this contact.</p>}
            </DrawerSection>
            {contact.assignments.length ? (
              <DrawerSection title="Formal Assignments">
                <div className="divide-y divide-slate-100">{contact.assignments.map((assignment) => (
                  <div key={String(assignment.id)} className="py-3 text-sm first:pt-0 last:pb-0">
                    <strong className="font-semibold text-slate-900">{String(assignment.associate_name ?? assignment.assignment_type ?? 'Assignment')}</strong>
                    <div className="mt-0.5 text-xs text-slate-500">{String(assignment.market_center_name ?? 'No Market Centre')} · {formatDate(assignment.effective_from)}</div>
                  </div>
                ))}</div>
              </DrawerSection>
            ) : null}
            <DrawerSection title="Integrations">
              <DetailGrid items={[
                ['Command CRM', contact.integrations.command === 'PAUSED' ? 'PAUSED / NOT CONNECTED' : contact.integrations.command],
                ['Google', contact.integrations.google],
              ]} />
            </DrawerSection>
          </div>
        )}
      </aside>
    </div>
  );
}

export default function CanonicalContactsWorkspace(): JSX.Element {
  const { token, activeContext } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [response, setResponse] = useState<ContactsResponse>({ rows: [], total: 0, limit: 25, offset: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(() => {
    const linked = searchParams.get('contactId');
    return linked && /^\d+$/.test(linked) ? linked : null;
  });
  const [detail, setDetail] = useState<CanonicalContactDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const search = searchParams.get('q') ?? '';
  const deferredSearch = useDeferredValue(search);
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);
  const pageSize = [25, 50, 100].includes(Number(searchParams.get('pageSize'))) ? Number(searchParams.get('pageSize')) : 25;

  const setFilter = (key: string, value: string): void => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (value) next.set(key, value); else next.delete(key);
      if (key !== 'page') next.set('page', '1');
      return next;
    }, { replace: true });
  };

  const filters = ['marketCentre', 'assignedAssociate', 'source', 'marketingStatus', 'createdFrom', 'createdTo', 'lastEnquiryFrom', 'lastEnquiryTo', 'contactSort', 'contactDirection'];
  const querySignature = [deferredSearch, page, pageSize, ...filters.map((key) => searchParams.get(key) ?? '')].join('|');

  useEffect(() => {
    if (!token || !activeContext?.id) return;
    let cancelled = false;
    const params = new URLSearchParams({ limit: String(pageSize), offset: String((page - 1) * pageSize) });
    if (deferredSearch) params.set('search', deferredSearch);
    for (const key of filters) {
      const value = searchParams.get(key);
      if (value) params.set(key === 'contactSort' ? 'sort' : key === 'contactDirection' ? 'direction' : key, value);
    }
    setLoading(true);
    setError(null);
    apiFetch(`/api/leads-contacts/contacts?${params}`, { headers: { Authorization: `Bearer ${token}`, 'X-Active-Context': activeContext.id } })
      .then(async (result) => {
        const body = (await result.json().catch(() => ({}))) as ContactsResponse & { error?: string };
        if (!result.ok) throw new Error(body.error ?? 'Contacts could not be loaded.');
        if (!cancelled) setResponse(body);
      })
      .catch((reason) => { if (!cancelled) setError(reason instanceof Error ? reason.message : 'Contacts could not be loaded.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  // querySignature captures the normalized set of URL filters.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, activeContext?.id, querySignature]);

  useEffect(() => {
    if (!selectedId || !token || !activeContext?.id) return;
    let cancelled = false;
    setDetailLoading(true);
    setDetail(null);
    apiFetch(`/api/leads-contacts/contacts/${selectedId}`, { headers: { Authorization: `Bearer ${token}`, 'X-Active-Context': activeContext.id } })
      .then(async (result) => {
        const body = (await result.json().catch(() => ({}))) as { contact?: CanonicalContactDetail; error?: string };
        if (!result.ok || !body.contact) throw new Error(body.error ?? 'Contact details could not be loaded.');
        if (!cancelled) setDetail(body.contact);
      })
      .catch((reason) => { if (!cancelled) setError(reason instanceof Error ? reason.message : 'Contact details could not be loaded.'); })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [selectedId, token, activeContext?.id]);

  const clearFilters = (): void => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      ['q', 'page', 'pageSize', ...filters].forEach((key) => next.delete(key));
      next.set('tab', 'contacts');
      return next;
    }, { replace: true });
  };

  const secondaryKeys = ['marketCentre', 'assignedAssociate', 'createdFrom', 'createdTo', 'lastEnquiryFrom', 'lastEnquiryTo'];
  const secondaryCount = secondaryKeys.filter((key) => searchParams.get(key)).length;
  const [showMore, setShowMore] = useState(secondaryCount > 0);
  const filtersActive = Boolean(search) || filters.some((key) => searchParams.get(key));

  const closeDetail = (): void => {
    setSelectedId(null);
    setDetail(null);
    if (searchParams.get('contactId')) {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.delete('contactId');
        return next;
      }, { replace: true });
    }
  };

  return (
    <div className="space-y-4">
      <section className="surface-card p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,2fr)_repeat(2,minmax(0,1fr))_auto] lg:items-end">
          <label className={`${labelClass} sm:col-span-2 lg:col-span-1`}>Search<input className={inputClass} value={search} onChange={(event) => setFilter('q', event.target.value)} placeholder="First name, surname, email, or mobile" /></label>
          <label className={labelClass}>Lead source<select className={inputClass} value={searchParams.get('source') ?? ''} onChange={(event) => setFilter('source', event.target.value)}><option value="">All sources</option><option value="PROPERTY24">Property24</option><option value="PRIVATE_PROPERTY">Private Property</option><option value="KWHOMES">KWHomes</option><option value="INTERNAL">Internal</option></select></label>
          <label className={labelClass}>Marketing status<select className={inputClass} value={searchParams.get('marketingStatus') ?? ''} onChange={(event) => setFilter('marketingStatus', event.target.value)}><option value="">All statuses</option><option value="CLEAR">Clear</option><option value="DO_NOT_CONTACT">Do not contact</option><option value="REVIEW_REQUIRED">Review required</option></select></label>
          <button type="button" aria-expanded={showMore} onClick={() => setShowMore((value) => !value)} className="rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">{showMore ? 'Fewer filters' : 'More filters'}{secondaryCount ? ` (${secondaryCount})` : ''}</button>
        </div>
        {showMore ? (
          <div className="mt-3 grid gap-3 border-t border-slate-100 pt-3 sm:grid-cols-2 lg:grid-cols-3">
            <fieldset className="grid grid-cols-2 gap-3 sm:col-span-2 lg:col-span-1 lg:grid-cols-1">
              <legend className="sr-only">Assignment</legend>
              <label className={labelClass}>Market Centre<input className={inputClass} value={searchParams.get('marketCentre') ?? ''} onChange={(event) => setFilter('marketCentre', event.target.value)} placeholder="Name or source ID" /></label>
              <label className={labelClass}>Assigned associate<input className={inputClass} value={searchParams.get('assignedAssociate') ?? ''} onChange={(event) => setFilter('assignedAssociate', event.target.value)} placeholder="Name or ID" /></label>
            </fieldset>
            <fieldset className="grid grid-cols-2 gap-3">
              <legend className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Created</legend>
              <label className={labelClass}>From<input className={inputClass} type="date" value={searchParams.get('createdFrom') ?? ''} onChange={(event) => setFilter('createdFrom', event.target.value)} /></label>
              <label className={labelClass}>To<input className={inputClass} type="date" value={searchParams.get('createdTo') ?? ''} onChange={(event) => setFilter('createdTo', event.target.value)} /></label>
            </fieldset>
            <fieldset className="grid grid-cols-2 gap-3">
              <legend className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Last enquiry</legend>
              <label className={labelClass}>From<input className={inputClass} type="date" value={searchParams.get('lastEnquiryFrom') ?? ''} onChange={(event) => setFilter('lastEnquiryFrom', event.target.value)} /></label>
              <label className={labelClass}>To<input className={inputClass} type="date" value={searchParams.get('lastEnquiryTo') ?? ''} onChange={(event) => setFilter('lastEnquiryTo', event.target.value)} /></label>
            </fieldset>
          </div>
        ) : null}
      </section>

      {error ? <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">{error}</div> : null}
      <section className="surface-card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div>
            <h2 className="font-semibold text-slate-900">Contacts</h2>
            <p className="text-xs text-slate-500">{loading ? 'Loading…' : `${response.total.toLocaleString()} canonical contact${response.total === 1 ? '' : 's'} · one person per contact, linked automatically from identifiers`}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-500">Sort<select aria-label="Sort contacts" className="rounded-md border border-slate-300 px-2 py-1 text-sm font-normal text-slate-800" value={searchParams.get('contactSort') ?? 'name'} onChange={(event) => setFilter('contactSort', event.target.value)}><option value="name">Contact name</option><option value="created">Created</option><option value="lastEnquiry">Last enquiry</option><option value="enquiries">Enquiry count</option></select></label>
            <button type="button" onClick={() => setFilter('contactDirection', (searchParams.get('contactDirection') ?? 'asc') === 'asc' ? 'desc' : 'asc')} className="rounded-md border border-slate-300 px-2 py-1 text-sm" aria-label="Toggle sort direction">{(searchParams.get('contactDirection') ?? 'asc') === 'asc' ? 'Ascending' : 'Descending'}</button>
            {filtersActive ? <button type="button" onClick={clearFilters} className="text-xs font-semibold text-sky-700 hover:underline">Clear filters</button> : null}
          </div>
        </div>
        {loading ? <p className="p-8 text-sm text-slate-500" role="status">Loading canonical contacts...</p>
          : response.rows.length ? <CanonicalContactsTable contacts={response.rows} onOpen={(contact) => setSelectedId(contact.id)} />
            : <div className="p-8"><CanonicalContactsEmptyState /></div>}
        {response.total > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            <label className="flex items-center gap-2">Rows per page<select value={pageSize} onChange={(event) => setFilter('pageSize', event.target.value)} className="rounded-md border border-slate-300 bg-white px-2 py-1"><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option></select></label>
            <span className="tabular-nums">Showing {response.offset + 1}&ndash;{Math.min(response.offset + response.limit, response.total)} of {response.total.toLocaleString()}</span>
            <div className="flex gap-2"><button type="button" disabled={page <= 1} onClick={() => setFilter('page', String(page - 1))} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 disabled:opacity-40">Previous</button><button type="button" disabled={response.offset + response.limit >= response.total} onClick={() => setFilter('page', String(page + 1))} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 disabled:opacity-40">Next</button></div>
          </div>
        ) : null}
      </section>

      {selectedId ? <CanonicalContactDrawer contact={detail} loading={detailLoading} onClose={closeDetail} /> : null}
    </div>
  );
}